import React, { useEffect, useRef, useState } from 'react'
import { Dialog, Popup, Toast } from 'antd-mobile'
import API_ROUTES from '../../api'
import httpClient from '../../shared/api/httpClient'
import './style.css'

export default function UploadImgToCND({
    value,
    onChange,
    memberInfo,
    type,
    adminDirect = false,
    oss,
    crop = true
}) {
    const inputRef = useRef(null)
    const cropImageRef = useRef(null)
    const dragRef = useRef({ dragging: false, x: 0, y: 0 })
    const [fileList, setFileList] = useState(value || [])
    const [cropVisible, setCropVisible] = useState(false)
    const [sourceFile, setSourceFile] = useState(null)
    const [sourceUrl, setSourceUrl] = useState('')
    const [zoom, setZoom] = useState(1)
    const [offset, setOffset] = useState({ x: 0, y: 0 })

    useEffect(() => {
        setFileList(value || [])
    }, [value])

    useEffect(() => {
        return () => {
            if (sourceUrl) URL.revokeObjectURL(sourceUrl)
        }
    }, [sourceUrl])

    const uploadFile = async (file) => {
        const formData = new FormData()
        formData.append('file', file)
        if (!oss) {
            formData.append('type', type)
            formData.append('memberId', memberInfo)
            if (adminDirect) formData.append('adminDirect', 'true')
        }

        const url = oss ? API_ROUTES.auth_uploadToOss : API_ROUTES.auth_uploadImg
        // 统一使用站内请求实例，确保头像上传携带真实登录人身份供后端鉴权。
        const res = await httpClient.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.data.code === 200) {
            typeof onChange === 'function' && onChange(res.data.data)
            Toast.show({ icon: 'success', content: '上传成功!' })
        } else {
            Toast.show({ icon: 'fail', content: res.data.msg || '上传失败' })
        }
        return res
    }

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        if (!file.type.startsWith('image/')) {
            Toast.show({ icon: 'fail', content: '请选择图片文件' })
            return
        }
        if (!crop) {
            await uploadFile(file)
            setFileList([{ url: URL.createObjectURL(file) }])
            return
        }
        if (sourceUrl) URL.revokeObjectURL(sourceUrl)
        setSourceFile(file)
        setSourceUrl(URL.createObjectURL(file))
        setZoom(1)
        setOffset({ x: 0, y: 0 })
        setCropVisible(true)
    }

    const createCroppedFile = () => new Promise((resolve, reject) => {
        const img = cropImageRef.current
        if (!img || !sourceFile) {
            reject(new Error('图片未加载'))
            return
        }
        const canvas = document.createElement('canvas')
        const outputSize = 900
        canvas.width = outputSize
        canvas.height = outputSize
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#f8fafc'
        ctx.fillRect(0, 0, outputSize, outputSize)

        const naturalWidth = img.naturalWidth
        const naturalHeight = img.naturalHeight
        const frameSize = 280
        const displayScale = Math.max(frameSize / naturalWidth, frameSize / naturalHeight) * zoom
        const displayWidth = naturalWidth * displayScale
        const displayHeight = naturalHeight * displayScale
        const displayLeft = (frameSize - displayWidth) / 2 + offset.x
        const displayTop = (frameSize - displayHeight) / 2 + offset.y
        const sourceX = Math.max(0, -displayLeft / displayScale)
        const sourceY = Math.max(0, -displayTop / displayScale)
        const sourceWidth = Math.min(naturalWidth - sourceX, frameSize / displayScale)
        const sourceHeight = Math.min(naturalHeight - sourceY, frameSize / displayScale)

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputSize, outputSize)
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('裁剪失败'))
                return
            }
            const filename = sourceFile.name.replace(/\.[^.]+$/, '') + '-crop.jpg'
            resolve(new File([blob], filename, { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.9)
    })

    const confirmCrop = async () => {
        try {
            Toast.show({ icon: 'loading', content: '上传中...', duration: 0 })
            // 头像先在前端裁剪成正方形，再沿用原来的上传接口进入审核流程。
            const croppedFile = await createCroppedFile()
            Toast.clear()
            await uploadFile(croppedFile)
            setFileList([{ url: URL.createObjectURL(croppedFile) }])
            setCropVisible(false)
        } catch (e) {
            Toast.clear()
            Toast.show({ icon: 'fail', content: e.message || '裁剪失败' })
        }
    }

    const onPointerDown = (e) => {
        dragRef.current = { dragging: true, x: e.clientX, y: e.clientY }
    }

    const onPointerMove = (e) => {
        if (!dragRef.current.dragging) return
        const dx = e.clientX - dragRef.current.x
        const dy = e.clientY - dragRef.current.y
        dragRef.current = { dragging: true, x: e.clientX, y: e.clientY }
        setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }))
    }

    const onPointerUp = () => {
        dragRef.current.dragging = false
    }

    const currentImage = fileList?.[0]?.url

    return (
        <div className="crop-upload">
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="crop-upload-input"
                onChange={handleFileChange}
            />
            <button type="button" className="crop-upload-trigger" onClick={() => inputRef.current?.click()}>
                {currentImage ? (
                    <img src={currentImage} alt="头像预览" />
                ) : (
                    <span>+</span>
                )}
            </button>
            {currentImage && (
                <button
                    type="button"
                    className="crop-upload-reset"
                    onClick={() => Dialog.confirm({
                        content: '是否重新上传',
                        onConfirm: () => inputRef.current?.click()
                    })}
                >
                    重传
                </button>
            )}

            <Popup
                visible={cropVisible}
                onMaskClick={() => setCropVisible(false)}
                bodyClassName="crop-popup"
                // 裁剪层可能从 Dialog 内触发，层级需高于全局 Dialog，避免图片编辑区被遮挡。
                style={{ '--adm-popup-z-index': 5200 }}
            >
                <div className="crop-popup-header">
                    <button type="button" onClick={() => setCropVisible(false)}>取消</button>
                    <strong>裁剪图片</strong>
                    <button type="button" onClick={confirmCrop}>上传</button>
                </div>
                <div className="crop-stage">
                    <div
                        className="crop-frame"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        {sourceUrl && (
                            <img
                                ref={cropImageRef}
                                src={sourceUrl}
                                alt="裁剪图片"
                                draggable={false}
                                style={{
                                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`
                                }}
                            />
                        )}
                    </div>
                    <div className="crop-tip">拖动调整位置，滑动缩放图片</div>
                    <input
                        className="crop-zoom"
                        type="range"
                        min="1"
                        max="2.4"
                        step="0.05"
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                    />
                </div>
            </Popup>
        </div>
    )
}
